import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { SideDrawerForm } from '@sales-center/components'
import { SalesTeam } from '@user/store/salesTeam/typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateLoadingState } from '@sales-center/common'
import { getStyles } from '../useStyles'
import { useCreateUpdatePosition } from '@user/store/salesTeam/hooks/useCreateUpdatePosition'
import {
  aggregatePositionsByTeamId,
  selectAllPositions,
} from '@user/store/salesTeam/slice'
import { useSelector } from 'react-redux'
import { SelectChangeEvent } from '@mui/material/Select/SelectInput'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { getValidParentsForPosition } from './utils'

interface Props {
  salesTeam: SalesTeam
  positionId?: number | null
  initialParentId: number | null
  title?: string
  onClose: () => void
}

export const AddPositionForm: FC<Props> = (props) => {
  const {
    salesTeam: { id: salesTeamId },
    onClose,
  } = props

  const dispatch = useAppDispatch()

  const {
    status: updatePositionStatus,
    execute: updatePosition,
    errors: updatePositionErrors,
  } = useCreateUpdatePosition()

  // ========== S T A T E ==========
  const [disableSave, setDisableSave] = useState(false)

  //#region title state
  const [parentId, setParentId] = useState<number | null>(props.initialParentId)
  const [title, setTitle] = useState('')
  const [titleTouched, setTitleTouched] = useState(false)
  const [titleValid, setTitleValid] = useState(false)
  const [titleErrors, setTitleErrors] = useState('')
  const positions = useSelector(selectAllPositions)
  // ========== H O O K S ==========
  const reportsToPositions = useMemo(
    () =>
      getValidParentsForPosition(
        aggregatePositionsByTeamId(salesTeamId, positions),
        props.positionId ?? null,
      ),
    [salesTeamId, positions, props.positionId],
  )

  const hydratedToPositions = useMemo(
    () => reportsToPositions.map((x) => x.title.toLowerCase()),
    [reportsToPositions],
  )

  useEffect(() => {
    setTitle(props.title ?? '')
  }, [props.title])

  useEffect(() => {
    setParentId(props.initialParentId)
  }, [props.initialParentId])

  useEffect(() => {
    setTitleValid(title.trim().length > 0 && title.length <= 30)
  }, [title])

  useEffect(() => {
    const matched = hydratedToPositions.includes(title.toLowerCase())

    if (matched) {
      setTitleValid(false)
      setTitleErrors('Title must be unique')
    }
  }, [hydratedToPositions, title])

  // ========== E V E N T   H A N D L E R S ==========

  const handleChangeReportsTo = React.useCallback(
    (event: SelectChangeEvent<number>) => {
      const newValue = event.target.value as number
      setParentId(newValue)
    },
    [],
  )

  const onChangeTitle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setTitleTouched(true)
      setTitle(value)
      setTitleErrors('')
    },
    [],
  )
  //#endregion

  //#region event handlers
  const onSave = useCallback(() => {
    updatePosition({
      id: props.positionId ?? undefined,
      salesTeamId,
      title,
      parentId: parentId === -1 ? null : parentId,
      users: [],
    })
  }, [updatePosition, props.positionId, salesTeamId, title, parentId])
  //#endregion

  //#region effects
  // display field errors
  useEffect(() => {
    const titleErrs = updatePositionErrors?.get('title')
    if (titleErrs) {
      setTitleValid(false)
      setTitleErrors(titleErrs.join(' '))
    }
  }, [updatePositionErrors])

  // disable save button
  useEffect(() => {
    if (updatePositionStatus == 'pending' || !titleValid) {
      setDisableSave(true)
    } else {
      setDisableSave(false)
    }
  }, [updatePositionStatus, titleValid])

  useEffect(() => {
    if (updatePositionStatus == 'pending') {
      dispatch(updateLoadingState('visible'))
    } else {
      dispatch(updateLoadingState('hidden'))
    }

    if (updatePositionStatus == 'success') {
      onClose()
    }
  }, [updatePositionStatus, dispatch, onClose])
  //#endregion
  const styles = getStyles(useTheme())
  return (
    <SideDrawerForm
      onSave={onSave}
      onCancel={onClose}
      disableSave={disableSave}
    >
      <Box sx={styles.form}>
        <Box sx={styles.formSection}>
          <span style={styles.formLabel}>Title</span>
          <TextField
            value={title}
            onChange={onChangeTitle}
            error={titleTouched && !titleValid}
            helperText={`${title.length}/30 ${titleErrors}`}
            label="Title"
            sx={styles.formControl}
            size="small"
            variant="outlined"
            required
          />

          {reportsToPositions.length > 0 && (
            <>
              <Typography component="span" sx={styles.formLabel}>
                Reports to
              </Typography>
              <Select
                sx={styles.select}
                margin="dense"
                displayEmpty
                onChange={handleChangeReportsTo}
                required
                value={parentId ?? -1}
                variant="outlined"
              >
                <MenuItem value={-1}>
                  <Typography component="em">Nobody</Typography>
                </MenuItem>
                {reportsToPositions.map((p) => (
                  <MenuItem key={p.id} value={p.id ?? ''}>
                    {p.title}
                  </MenuItem>
                ))}
              </Select>

              <Typography component="small" sx={styles.smallPrint}>
                Users with the position selected above will have access to
                records owned by users with this position.
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </SideDrawerForm>
  )
}
